import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SearchIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { CsvDownloadButton } from "@/components/analytics/CsvDownloadButton";
import { PaginationBar } from "@/components/PaginationBar";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { formatDateTime, formatNumber } from "@/lib/format";
import { num } from "@/lib/matching";
import { deviceLabel, locationLabel } from "./labels";

type UserRow = components["schemas"]["UserActivityUserRow"];

const COLUMN_COUNT = 7;

/** Every user in scope, most recently active first. Root-mounted, so organisations (with their client) show. */
export function UserActivityUsersTable({ clientIds, from, to }: { clientIds: string[]; from: string; to: string }) {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 400);
  const pagination = useCursorPagination({ initialLimit: 20 });
  const [rows, setRows] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const scopeKey = `${clientIds.join(",")}|${from}|${to}|${debouncedSearch}`;
  const { reset } = pagination;

  useEffect(() => {
    reset();
  }, [scopeKey, reset]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setFailed(false);
      const { data, error } = await api.GET("/admin/user-activity/users", {
        params: {
          query: {
            From: from,
            To: to,
            ClientIds: clientIds.length > 0 ? clientIds : undefined,
            Search: debouncedSearch || undefined,
            Cursor: pagination.cursor,
            Limit: pagination.limit,
          },
        },
      });
      if (cancelled) return;
      if (error || !data) {
        setFailed(true);
        toast.error("Users could not be loaded.");
      } else {
        setRows(data.data ?? []);
        pagination.setPage({
          nextCursor: data.meta?.cursors?.next,
          previousCursor: data.meta?.cursors?.previous,
        });
      }
      setLoading(false);
    }
    void load();
    return () => {
      cancelled = true;
    };
    // pagination.setPage is stable; scopeKey stands in for its inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey, pagination.cursor, pagination.limit]);

  const csvParams = new URLSearchParams();
  for (const id of clientIds) csvParams.append("ClientIds", id);
  csvParams.set("From", from);
  csvParams.set("To", to);
  if (debouncedSearch) csvParams.set("Search", debouncedSearch);

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="relative w-72">
          <SearchIcon className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or email"
            className="h-8 pl-8"
            aria-label="Search users"
          />
        </div>
        <CsvDownloadButton path="/admin/user-activity/users" params={csvParams} />
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Organisations</TableHead>
              <TableHead>Last active</TableHead>
              <TableHead>Last sign-in</TableHead>
              <TableHead className="text-right">Sign-ins</TableHead>
              <TableHead>Last location</TableHead>
              <TableHead>Last device</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && rows.length === 0 ? (
              Array.from({ length: 5 }, (_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={COLUMN_COUNT}>
                    <div className="bg-muted h-5 animate-pulse rounded" />
                  </TableCell>
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className="text-muted-foreground h-24 text-center">
                  {failed ? "Users could not be loaded." : "No users match."}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.userId}>
                  <TableCell className="max-w-64">
                    <Link to={`/users/${row.userId}`} className="text-primary block truncate font-medium hover:underline">
                      {row.name || row.email}
                    </Link>
                    {row.name && <span className="text-muted-foreground block truncate text-xs">{row.email}</span>}
                  </TableCell>
                  <TableCell className="max-w-64">
                    <span className="block truncate text-sm">
                      {row.organisations.map((o) => o.organisationName).join(", ") ||
                        row.clients.map((c) => `${c.clientName} (client)`).join(", ") || (
                          <span className="text-muted-foreground">None</span>
                        )}
                    </span>
                    {row.organisations.length > 0 && (
                      <span className="text-muted-foreground block truncate text-xs">
                        {[...new Set(row.organisations.map((o) => o.clientName))].join(", ")}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm">
                    {row.lastActiveAt ? formatDateTime(row.lastActiveAt) : <span className="text-muted-foreground">Never</span>}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm">
                    {row.lastSignInAt ? formatDateTime(row.lastSignInAt) : <span className="text-muted-foreground">Never</span>}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{formatNumber(num(row.signInsInWindow))}</TableCell>
                  <TableCell className="text-sm">
                    {locationLabel(row.lastCity, row.lastCountry) ?? <span className="text-muted-foreground">Unknown</span>}
                  </TableCell>
                  <TableCell className="text-sm">
                    {deviceLabel(row.lastDeviceType, row.lastOs, row.lastBrowser) ?? (
                      <span className="text-muted-foreground">Unknown</span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <PaginationBar
        hasPrevious={pagination.hasPrevious}
        hasNext={pagination.hasMore}
        isLoading={loading}
        limit={pagination.limit}
        onPrevious={pagination.goPrevious}
        onNext={pagination.goNext}
        onLimitChange={pagination.setLimit}
      />
    </div>
  );
}
